import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { isEqual } from 'lodash';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { get } from 'lodash';

import { UIStatesEnum } from 'common/src/models';
import { AppState } from '../../../store/state/app.state';
import { ScheduledPaymentModel } from '../../models';
import { selectScheduledPayment, selectScheduledPaymentState } from '../../store/selectors';
import { ScheduledPaymentApiService } from '../../services/scheduled-payment-api.service';
import { UpdatePropertiesBlockValid } from '../../store/actions/scheduled-payment.actions';
import { ScheduledPaymentListTabsEnum } from '../../enums';
import { selectAccountingSettings } from '../../../store/selectors/shared.selectors';
import { AccountingSettingsModel } from '../../../accounting/accounting-settings-module/models';
import { CommonModalsActionsEnum, ConfirmModalComponent } from 'common/src/modules/modals/modals-common';
import { UPDATE_SMALL_BUSINESS_MODAL_CONFIG } from '../../../shared/constants';

@Component({
  selector: 'rnpl-scheduled-payment-total-info',
  templateUrl: './scheduled-payment-total-info.component.html',
  styleUrls: ['./scheduled-payment-total-info.component.scss']
})
export class ScheduledPaymentTotalInfoComponent implements OnInit, AfterViewInit {

  @ViewChild('properties', {static: false}) properties: TemplateRef<any>;
  @ViewChild('propertiesPartner', {static: false}) propertiesPartner: TemplateRef<any>;

  @Input() public selectedMenuItem: string;

  public accountingSettings: AccountingSettingsModel;
  public workspaceCurrency = 'EUR';
  public sidebarStatus: UIStatesEnum = UIStatesEnum.VIEW;
  public isReadonly: boolean = true;

  public scheduledPayment: ScheduledPaymentModel;
  public form: FormGroup;

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly scheduledPaymentApiService: ScheduledPaymentApiService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.trackScheduledPaymentChanges();
    this.trackScheduledPaymentStateChanges();

    this.store.select(selectAccountingSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: AccountingSettingsModel) => {
        this.accountingSettings = settings
        this.workspaceCurrency = settings.currencyCode;
      });
  }

  private trackScheduledPaymentStateChanges(): void {
    this.store.select(selectScheduledPaymentState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.sidebarStatus = state;
        this.isReadonly = state === UIStatesEnum.VIEW;
        this.setControlsState();
      });
  }

  private trackScheduledPaymentChanges(): void {
    this.store.select(selectScheduledPayment)
      .pipe(
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$)
      )
      .subscribe((scheduledPayment: ScheduledPaymentModel) => {
        this.scheduledPayment = scheduledPayment;
        this.initForm();
        this.currencyControl.patchValue(get(scheduledPayment, 'properties.currency'));
        this.exchangeRateControl.patchValue(get(scheduledPayment, 'properties.exchangeRate'));
        this.smallBusinessControl.patchValue(get(scheduledPayment, 'properties.smallBusiness'));
        this.partnerIdControl.patchValue(get(scheduledPayment, 'properties.partnerProfile.id'));
        this.partnerTypeControl.patchValue(get(scheduledPayment, 'properties.partnerProfile.type'));
        this.costCenterIdControl.patchValue(get(scheduledPayment, 'properties.costCenter.id'));
        this.branchOfficeIdControl.patchValue(get(scheduledPayment, 'properties.branchOffice.id'));

        if (this.scheduledPayment.properties.currency !== this.workspaceCurrency) {
          this.exchangeRateControl.setValidators(Validators.required);
          this.store.dispatch(UpdatePropertiesBlockValid({
            propertiesBlockValid: this.currencyControl.valid && this.exchangeRateControl.valid && !!this.exchangeRateControl.value})
          );
        } else {
          this.exchangeRateControl.clearValidators();
          this.store.dispatch(UpdatePropertiesBlockValid({
            propertiesBlockValid: this.currencyControl.valid})
          );
        }
        this.exchangeRateControl.updateValueAndValidity();

        this.setControlsState();
      });
  }

  private setControlsState(): void {
    if (this.isReadonly) {
      this.form.disable({emitEvent: false});
      return;
    } else {
      this.form.enable({emitEvent: false});

      if (this.scheduledPayment.status === ScheduledPaymentListTabsEnum.OPEN) {
        this.partnerIdControl.disable({emitEvent: false});
        this.branchOfficeIdControl.disable({emitEvent: false});
        this.partnerTypeControl.disable({emitEvent: false});
      }
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      properties: this.fb.group({
        partnerProfile: this.fb.group({
          id: [null, [Validators.required]],
          type: [null, [Validators.required]],
        }),
        exchangeRate: [null, [Validators.required]],
        currency: [null, [Validators.required]],
        smallBusiness: [null, {updateOn: 'change'}],
        costCenter: this.fb.group({
          id: [],
        }),
        branchOffice: this.fb.group({
          id: [],
        }),
      }),
    }, {updateOn: 'blur'});
  }

  public updateField(fieldName: string, fieldValue: any): void {
    if (fieldName === 'branchOfficeId') { fieldName = 'properties.branchOffice.id'; }
    this.scheduledPaymentApiService.updateScheduledPaymentField(this.scheduledPayment.id, fieldName, fieldValue)
      .subscribe(); // updated via store
  }

  public updateSmallBusiness(fieldName: string, fieldValue): void {
    if (this.accountingSettings.smallBusiness.forward === fieldValue) {
      this.updateField(fieldName, fieldValue);
      return;
    }

    const dialog = this.dialog.open(ConfirmModalComponent, UPDATE_SMALL_BUSINESS_MODAL_CONFIG);

    dialog.afterClosed().subscribe((res: CommonModalsActionsEnum) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.updateField(fieldName, fieldValue);
      } else if (res === CommonModalsActionsEnum.REJECT) {
        this.router.navigate(['/accounting/settings/general-settings']);
      } else {
        this.smallBusinessControl.patchValue(this.scheduledPayment.properties.smallBusiness, {emitEvent: false});
      }
    });
  }

  public isView(): boolean {
    return this.sidebarStatus === UIStatesEnum.VIEW;
  }

  get partnerIdControl(): FormControl { return this.form.get('properties').get('partnerProfile').get('id') as FormControl; }
  get partnerTypeControl(): FormControl { return this.form.get('properties').get('partnerProfile').get('type') as FormControl; }
  get exchangeRateControl(): FormControl { return this.form.get('properties').get('exchangeRate') as FormControl; }
  get currencyControl(): FormControl { return this.form.get('properties').get('currency') as FormControl; }
  get smallBusinessControl(): FormControl { return this.form.get('properties').get('smallBusiness') as FormControl; }
  get costCenterIdControl(): FormControl { return this.form.get('properties').get('costCenter').get('id') as FormControl; }
  get branchOfficeIdControl(): FormControl { return this.form.get('properties').get('branchOffice').get('id') as FormControl; }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
